import * as React from "react";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import { Link } from "react-router-dom";
import { QueryTable, QueryTableColumn } from "../misc/components/queryTable/QueryTable";
import { AuditTrailEvent, Query } from "../misc/api/apiTypes";
import { formatNull } from "../misc/components/Formatters/Formatters";
import {
    PageAction,
    SortAction,
    ReceiveQueryAction,
    QueryTableState,
    QueryTableActions,
    FilterAction
} from "../misc/components/queryTable/Hooks/UseQueryTable";
import { LocalizedDateTime, DateTimeAccuracy } from "../misc/components/LocalizedDateTime";
import EventDateTimeFilterModal, {
    EventDateTimeFilterMember,
    EventDateTimeHeader
} from "./EventDateTimeFilterModal";
import UserFilterModal, { UserHeader } from "./UserFilterModal";
import AuditTrailActionFilterModal, { AuditTrailActionHeader } from "./AuditTrailActionFilterModal";
import { useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

export interface AuditTrailListTableProps {
    queryTableReducer: [QueryTableState, React.Dispatch<QueryTableActions>];
}

export const AuditTrailListTable = ({
    queryTableReducer
}: AuditTrailListTableProps): JSX.Element => {
    const legacyClient = useWebPortalLegacyClient();

    const refreshData = () => {
        setIsLoading(true);

        legacyClient.getAuditTrailEvents(queryState.query).then(_populatedQuery => {
            updateQueryData({
                ..._populatedQuery,
                Page: {
                    ..._populatedQuery.Page,
                    Length: 10
                }
            });
            setIsLoading(false);
        });
    };

    const RenderHamburgerMenu = (id: string) => {
        return (
            <HamburgerMenu>
                <li>
                    <Link to={`/Audit/Event/${id}`}>View</Link>
                </li>
            </HamburgerMenu>
        );
    };

    const [queryState, dispatch] = queryTableReducer;
    const [selectedFilterModal, setSelectedFilterModal] = React.useState<string>(null);

    React.useEffect(
        () => {
            refreshData();
        },
        [
            queryState.query.Page.Current,
            queryState.query.Order.Member,
            queryState.query.Order.Direction,
            queryState.query.Filters,
        ]
    );

    const [isLoading, setIsLoading] = React.useState(false);
    const updateQueryData = (query: Query<AuditTrailEvent>) =>
        dispatch(ReceiveQueryAction.create(query));
    const handlePage = (page: number) => !isLoading && dispatch(PageAction.create(page));
    const handleSort = (member: string) => dispatch(SortAction.create(member));
    const handleFilter = (member: string) => setSelectedFilterModal(member);

    const UsernameFilterMember = "User.Username";
    const ActionNameFilterMember = "ActionName";

    const getModal = () => {
        switch (selectedFilterModal) {
            case EventDateTimeFilterMember:
                return (
                    <EventDateTimeFilterModal
                        query={queryState.query}
                        onApply={(from, to) => {
                            dispatch(FilterAction.create(from.Member, from.Condition, from.Values));
                            dispatch(FilterAction.create(to.Member, to.Condition, to.Values));
                            setSelectedFilterModal(null);
                        }}
                        onClose={() => setSelectedFilterModal(null)}
                    />
                );
            case UsernameFilterMember:
                return (
                    <UserFilterModal
                        filterMember={UsernameFilterMember}
                        query={queryState.query}
                        onApply={(member, condition, values) => {
                            dispatch(FilterAction.create(member, condition, values));
                            setSelectedFilterModal(null);
                        }}
                        onClose={() => setSelectedFilterModal(null)}
                    />
                );
            case ActionNameFilterMember:
                return (
                    <AuditTrailActionFilterModal
                        filterMember={ActionNameFilterMember}
                        query={queryState.query}
                        onApply={(member, condition, values) => {
                            dispatch(FilterAction.create(member, condition, values));
                            setSelectedFilterModal(null);
                        }}
                        onClose={() => setSelectedFilterModal(null)}
                    />
                );
        }
    };

    return (
        <>
            <QueryTable
                query={queryState.query}
                rowUniqueIdentifier={row => row.Id}
                onPage={handlePage}
                onSort={handleSort}
                onFilter={handleFilter}
                isLoading={isLoading}
            >
                <QueryTableColumn
                    member={EventDateTimeFilterMember}
                    header={EventDateTimeHeader}
                    map={(row: AuditTrailEvent) => (
                        <LocalizedDateTime
                            time={row.EventDateTime}
                            accuracy={DateTimeAccuracy.Second}
                            isTooltip={true}
                        />
                    )}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={UsernameFilterMember}
                    header={UserHeader}
                    map={(row: AuditTrailEvent) => formatNull(row.User ? row.User.Username : null)}
                    sortable={false}
                    filterable={true}
                />
                <QueryTableColumn
                    member={ActionNameFilterMember}
                    header={AuditTrailActionHeader}
                    map={(row: AuditTrailEvent) => formatNull(row.ActionName)}
                    sortable={false}
                    filterable={true}
                />
                <QueryTableColumn
                    member="SummaryText"
                    header="Summary"
                    map={(row: AuditTrailEvent) => formatNull(row.SummaryText)}
                    sortable={false}
                    filterable={false}
                />

                <QueryTableColumn
                    member=""
                    header=""
                    map={(row: AuditTrailEvent) => RenderHamburgerMenu(row.Id)}
                    sortable={false}
                    filterable={false}
                />
            </QueryTable>
            {getModal()}
        </>
    );
};
